'use client';

import React, { useState, useEffect, useMemo } from 'react';
import ClientsHeader from '@/components/dashboard/clients/ClientsHeader';
import ClientsKpiStrip from '@/components/dashboard/clients/ClientsKpiStrip';
import ClientsFilterBar from '@/components/dashboard/clients/ClientsFilterBar';
import ClientsFilterPills from '@/components/dashboard/clients/ClientsFilterPills';
import ClientsTable, { ClientRecord } from '@/components/dashboard/clients/ClientsTable';
import ClientDetailView from '@/components/dashboard/clients/ClientDetailView';
import NewClientModal from '@/components/dashboard/clients/NewClientModal';

export default function ClientsPage() {
  const [viewMode, setViewMode] = useState<'LIST' | 'DETAIL'>('LIST');
  const [selectedClient, setSelectedClient] = useState<ClientRecord | null>(null);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState<'ALL' | 'ACTIVE' | 'INACTIVE' | 'NEVER'>('ALL');

  // Datos
  const [clients, setClients] = useState<ClientRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [newClientModalOpen, setNewClientModalOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 4000);
  };

  // Cargar clientes desde PostgreSQL
  const fetchClients = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/clients');
      const data = await res.json();
      if (data.success) {
        setClients(data.clients || []);
        if (data.clients?.length > 0 && !selectedRowId) {
          setSelectedRowId(data.clients[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // Métricas de Retención calculadas de forma segura en useEffect
  const [kpis, setKpis] = useState({
    total: 0,
    newIn30: 0,
    never: 0,
    active: 0,
    inactive: 0
  });

  useEffect(() => {
    const total = clients.length;
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;

    let newIn30 = 0;
    let never = 0;
    let active = 0;
    let inactive = 0;

    clients.forEach(c => {
      const createdAt = new Date(c.createdAt).getTime();
      if (createdAt >= thirtyDaysAgo) newIn30 += 1;

      if (c.appointmentsCount === 0) {
        never += 1;
      } else {
        active += 1;
      }
    });

    setKpis({
      total,
      newIn30,
      never,
      active,
      inactive
    });
  }, [clients]);

  // Filtrado de clientes
  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      // Buscador
      if (searchTerm.trim()) {
        const q = searchTerm.toLowerCase();
        const matchName = c.name.toLowerCase().includes(q);
        const matchPhone = c.phone.includes(q);
        const matchEmail = c.email?.toLowerCase().includes(q);
        if (!matchName && !matchPhone && !matchEmail) return false;
      }

      // Pastilla de Estado
      if (selectedFilter === 'ACTIVE' && c.appointmentsCount === 0) return false;
      if (selectedFilter === 'NEVER' && c.appointmentsCount > 0) return false;
      if (selectedFilter === 'INACTIVE' && (c.appointmentsCount === 0 || c.appointmentsCount > 0)) {
        // En base a visitas históricas > 90 días si aplica
      }

      return true;
    });
  }, [clients, searchTerm, selectedFilter]);

  // Crear nuevo cliente
  const handleCreateClient = async (formData: any) => {
    const res = await fetch('/api/clients', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData)
    });
    const data = await res.json();
    if (!data.success) {
      throw new Error(data.error || 'No se pudo crear el cliente');
    }

    showToast(`¡Cliente ${formData.name} registrado con éxito!`);
    fetchClients();
  };

  // Actualizar datos / notas de cliente
  const handleUpdateClient = async (clientId: string, updateData: any) => {
    const res = await fetch('/api/clients', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: clientId, ...updateData })
    });
    const data = await res.json();
    if (data.success) {
      showToast('¡Ficha del cliente actualizada!');
      fetchClients();
      if (selectedClient && selectedClient.id === clientId) {
        setSelectedClient({ ...selectedClient, ...updateData });
      }
    }
  };

  const handleOpenDetail = (client: ClientRecord) => {
    setSelectedClient(client);
    setSelectedRowId(client.id);
    setViewMode('DETAIL');
  };

  const handleOpenSelectedFromBar = () => {
    const found = clients.find(c => c.id === selectedRowId) || clients[0];
    if (found) {
      handleOpenDetail(found);
    }
  };

  return (
    <div className="space-y-4">
      {/* Toast flotante */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-slate-900 text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 text-sm font-semibold animate-in slide-in-from-bottom-5 duration-200">
          <span className="w-2.5 h-2.5 rounded-full bg-emerald-400"></span>
          <span>{toastMessage}</span>
        </div>
      )}

      {viewMode === 'DETAIL' && selectedClient ? (
        /* Vista Ficha 360° del Cliente */
        <ClientDetailView
          client={selectedClient}
          onBack={() => setViewMode('LIST')}
          onUpdateClient={handleUpdateClient}
        />
      ) : (
        /* Vista Principal: Listado y CRM */
        <>
          {/* 1. Cabecera con Título, Conteo y Accesos Rápidos */}
          <ClientsHeader
            clientsCount={filteredClients.length}
            onOpenNewClientModal={() => setNewClientModalOpen(true)}
          />

          {/* 2. Tira de 4 KPIs de Retención de Clientes */}
          <ClientsKpiStrip
            totalClients={kpis.total}
            newIn30Days={kpis.newIn30}
            neverCame={kpis.never}
            activeCount={kpis.active}
            inactiveCount={kpis.inactive}
          />

          {/* 3. Barra de Herramientas y Botones Principales */}
          <ClientsFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onOpenSelectedProfile={handleOpenSelectedFromBar}
            onOpenImport={() => alert('Opción de importación de contactos disponible.')}
            onOpenNewClient={() => setNewClientModalOpen(true)}
            hasSelectedClient={Boolean(selectedRowId)}
          />

          {/* 4. Pastillas de Estado [Todas | Activas | Inactivas | Nunca vinieron] */}
          <ClientsFilterPills
            selectedFilter={selectedFilter}
            onSelectFilter={setSelectedFilter}
            counts={{
              all: kpis.total,
              active: kpis.active,
              inactive: kpis.inactive,
              never: kpis.never
            }}
          />

          {/* 5. Tabla de Clientes con Avatares en Pastel y Ordenación */}
          <ClientsTable
            clients={filteredClients}
            loading={loading}
            selectedClientId={selectedRowId}
            onSelectClient={setSelectedRowId}
            onOpenDetail={handleOpenDetail}
          />

          {/* MODAL: Nuevo Cliente */}
          <NewClientModal
            isOpen={newClientModalOpen}
            onClose={() => setNewClientModalOpen(false)}
            onSubmit={handleCreateClient}
          />
        </>
      )}
    </div>
  );
}
